import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';

import PropTypes from 'prop-types';

import { useCurrentProjectId } from '@adsk/alloy-provider';
import Button from '@adsk/alloy-react-button';
import { noop } from '@adsk/alloy-react-helpers';
import { useLocale } from '@adsk/alloy-react-locale';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';
import VirtualizedTree from '@adsk/nirvana-virtualized-tree';

import EntityPickerModalError from '../../../modal/EntityPickerModalError';
import { formatEntityBlock } from '../helpers/formatEntities';
import {
  INNER_NODES,
  isInitializingTree,
  isCancelError,
  isNodeSelectable,
  isNodeExpandable,
  getEntityIcon,
  genEntityDisplayLabel,
  getFolderContents,
  addNodes,
  addLoadingNode,
  addRetryNode,
  cleanFolderContent,
  cleanFolderPagination,
  cancelFolderRequest,
  isBuiltInNode,
  getImplicitSelectedKeys,
  customizeFileFilter,
  getExplicitNodes,
  getTopFolders,
  getNodesByKeys,
  getNodeByKey,
  getDefaultExpandKeys,
  sortFolders,
} from '../helpers/tree';
import { differenceBy } from '../helpers/utils';

const flexStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const viewItemStyle = {
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  flex: 'auto',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const PAGINATION = {
  folder: {
    limit: 200,
    offset: 0,
    hasNext: false,
    isLoadedAll: false,
  },
  file: {
    limit: 200,
    offset: 0,
    hasNext: false,
    isLoadedAll: false,
  },
};

const TreeView = React.forwardRef(
  (
    {
      plugin,
      entities,
      disabledIds: disabledKeys,
      selectedIds: selectedKeys,
      setSelection,
      multiple,
      dataViewProps,
      valueKey,
      ...rest
    },
    ref,
  ) => {
    const projectId = useCurrentProjectId();
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    const defaultFolderUrn = plugin.getDefaultExpandedFolderUrn?.();
    const [isInitializing, setIsInitializing] = useState(
      isInitializingTree(entities),
    );
    const [isInitializeError, setIsInitializeError] = useState(false);
    const [nodes, setNodes] = useState([]);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [selectedNodes, setSelectedNodes] = useState([]);
    const paginationRef = useRef(new Map());
    const {
      dataFilter,
      enableFoldersSelection: isMixMode,
      allowWholeFolderSelection: enableSelectAll, // includes/excludes all descendants
      rowIdsWithIndicator,
      renderIndicator,
    } = dataViewProps;
    const implicitSelectedKeys = useMemo(
      () =>
        enableSelectAll
          ? getImplicitSelectedKeys(nodes, selectedKeys, valueKey)
          : [],
      [nodes, selectedKeys, valueKey, enableSelectAll],
    );
    const expandedIds = useMemo(
      () => expandedKeys.map(({ id, urn }) => (valueKey === 'urn' ? urn : id)),
      [expandedKeys, valueKey],
    );

    const loadContents = useCallback(
      async (node, nodes) => {
        if (node.readonly) return nodes;

        const nodePagination = paginationRef.current.get(node.urn);

        if (!nodePagination) {
          paginationRef.current.set(node.urn, {
            path: node.path,
            ...PAGINATION,
          });
          setExpandedKeys([
            ...expandedKeys,
            {
              urn: node.urn,
              path: node.path,
              id: node.id,
              idPath: node.idPath,
            },
          ]);
        }

        let updatedNodes = nodes;
        try {
          const { folders, files, nextFolderPage, nextFilePage, folderUrn } =
            await getFolderContents(
              projectId,
              node,
              paginationRef.current.get(node.urn),
            );
          paginationRef.current.set(folderUrn, {
            folder: nextFolderPage,
            file: nextFilePage,
            path: node.path,
          });
          const filteredFiles = dataFilter
            ? customizeFileFilter(files, dataFilter)
            : files;
          updatedNodes = addNodes(nodes, node, [...folders, ...filteredFiles]);
          setNodes(updatedNodes);
        } catch (error) {
          // swallow all error here. attach a retry node
          if (!isCancelError(error)) {
            updatedNodes = addRetryNode(nodes, node.urn);
            setNodes(updatedNodes);
          }
        }
        return updatedNodes;
      },
      [projectId, dataFilter, expandedKeys],
    );

    useEffect(() => {
      if (!isInitializing) return;

      /**
       *  list top folders when without defaultFolderUrn
       *  list folderThee and all sub-folders and first page files in the path wen given defaultFolderUrn
       */
      setIsInitializeError(false);
      const getFolders = async () => {
        try {
          const folders = await getTopFolders(projectId, defaultFolderUrn);
          const sortedFolders = sortFolders(folders, true);
          setNodes(sortedFolders);

          let toUpdatedFolders = sortedFolders;

          if (defaultFolderUrn) {
            const target = getNodeByKey(folders, defaultFolderUrn, 'urn');
            if (target) {
              const defaultExpandKeys = getDefaultExpandKeys(target);
              setExpandedKeys(defaultExpandKeys);
              for (let i = 0; i < defaultExpandKeys.length; i++) {
                const node = defaultExpandKeys[i];
                paginationRef.current.set(node.urn, {
                  path: node.path,
                  folder: {
                    hasNext: false,
                    isLoadedAll: true, // skip load sub folders, only load documents
                  },
                  file: {
                    limit: 200,
                    offset: 0,
                    hasNext: false,
                    isLoadedAll: false,
                  },
                });
                toUpdatedFolders = await loadContents(node, toUpdatedFolders);
              }
            }
          }
          setIsInitializing(false);
        } catch (err) {
          setIsInitializing(false);
          // storybook hijacks https error. no cancel error outside storybook.
          if (!isCancelError(err)) {
            setIsInitializeError(true);
          }
        }
      };
      getFolders();

      // todo: de-coupling loadContents dependency and add loadContents. currently loadContents will cause extra api if added into hooks dependency.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, isInitializing, isInitializeError, defaultFolderUrn]);

    const checkNodeDisable = useCallback(
      (node) => {
        if (isBuiltInNode(node)) return false;

        if (disabledKeys.includes(node[valueKey])) return true;

        // dataFilter may set node disabled
        if (node.disabled) return true;

        if (enableSelectAll) {
          return (
            !selectedKeys.includes(node[valueKey]) &&
            selectedKeys.some((value) =>
              node[valueKey === 'id' ? 'idPath' : 'path']?.includes(value),
            )
          );
        }

        return false;
      },
      [enableSelectAll, disabledKeys, selectedKeys, valueKey],
    );

    const checkNodeSelectable = useCallback(
      (node) => isNodeSelectable(node, isMixMode),
      [isMixMode],
    );

    // strategy: load folders with high priority. If folders loaded, load files at same time.
    // which means, when all folders loaded, load first page files at once.
    const handleOpenFolder = useCallback(
      (node) => loadContents(node, nodes),
      [loadContents, nodes],
    );

    const handleSelectNodes = useCallback(
      (updatedKeys) => {
        // cache selected nodes, nodes tree content cleared when folder closed.
        const cachedNodes = getNodesByKeys(
          selectedNodes,
          updatedKeys,
          valueKey,
        );
        const toSelectNodes = enableSelectAll
          ? getExplicitNodes(nodes, selectedKeys, updatedKeys, valueKey)
          : getNodesByKeys(nodes, updatedKeys, valueKey);
        const difference = differenceBy(cachedNodes, toSelectNodes, valueKey);
        const pickedNodes = [...difference, ...toSelectNodes];
        setSelectedNodes(pickedNodes);
        const selections = pickedNodes.filter(
          (node) => !disabledKeys.includes(node[valueKey]),
        );
        setSelection(selections.map(formatEntityBlock));
      },
      [
        enableSelectAll,
        nodes,
        selectedKeys,
        setSelection,
        valueKey,
        disabledKeys,
        selectedNodes,
      ],
    );

    const handleLoadMore = useCallback(
      (node) => {
        const updatedNodes = addLoadingNode(nodes, node.urn);
        setNodes(updatedNodes);
        loadContents(node, nodes);
      },
      [loadContents, nodes],
    );

    /**
     * Background: The VirtualizedTree manages loading/loadError UI component and ONLY inject loading/loadError state into nodeTree when expand node.
     *
     * After clicked loadMore button, this loading/loadError nodes are outside VirtualizedTree and still loading/loadError UI
     * rendered by VirtualizedTree.
     *
     * Solution:
     * 1. VirtualizedTree renders loading/loadError UI component, TreeView renders loadMore UI component.
     * 2. loadContents method swallows all errors and manages:
     *    loadError state when error happens in expand node (skip the control in the background) and load more.
     */
    const renderViewItem = useCallback(
      ({ node }) => {
        if (
          node[VirtualizedTree.NODE_KEYS.NODE_ACTION_TYPE] ===
          INNER_NODES.LOAD_MORE
        ) {
          return (
            <Button
              variant={Button.VARIANTS.TERTIARY}
              onClick={() => handleLoadMore(node)}
            >
              {T.translate('LOAD_MORE')}
            </Button>
          );
        }
        const EntityIcon = getEntityIcon(node);
        const showCustomIndicator =
          renderIndicator && rowIdsWithIndicator.includes(node.urn);

        return (
          <div style={viewItemStyle}>
            <EntityIcon style={{ marginRight: '8px', flexShrink: '0' }} />
            <OverflowTooltip
              content={genEntityDisplayLabel(node)}
              getTargetProps={() => ({
                style: { overflow: 'hidden', width: '100%' },
              })}
            >
              {genEntityDisplayLabel(node)}
            </OverflowTooltip>
            {showCustomIndicator && renderIndicator(node)}
          </div>
        );
      },
      [T, handleLoadMore, renderIndicator, rowIdsWithIndicator],
    );

    const handleCloseFolder = (node) => {
      cancelFolderRequest(node.urn);
      setExpandedKeys(
        expandedKeys.filter(({ path }) => !path.includes(node.path)),
      );
      const updatedNodes = cleanFolderContent(nodes, node.urn);
      setNodes(updatedNodes);
      paginationRef.current = cleanFolderPagination(
        paginationRef.current,
        node.urn,
        node.path,
      );
    };

    if (isInitializing) {
      return (
        <div style={flexStyle}>
          <ProgressRing size={ProgressRing.SIZES.MEDIUM} />
        </div>
      );
    }

    if (isInitializeError) {
      return (
        <div style={flexStyle}>
          <EntityPickerModalError onTryAgain={() => setIsInitializing(true)} />
        </div>
      );
    }

    return (
      <div style={{ height: '100%', overflow: 'hidden' }}>
        <VirtualizedTree
          {...rest}
          ref={ref}
          enableShiftSelection
          enableCrossParentSelection
          matchAlloyStyle
          rowHeight={48}
          getNodeStyle={() => ({
            borderBottom: '1px solid rgba(238, 238, 238, 1)',
          })}
          childrenKey="folders"
          retryButtonText={T.translate('RETRY')}
          emptyResultText={T.translate('EMPTY')}
          valueKey={valueKey}
          multiple={multiple}
          selectedKeys={[...selectedKeys, ...implicitSelectedKeys]}
          nodes={nodes}
          expandedKeys={expandedIds}
          isNodeDisabled={checkNodeDisable}
          isNodeSelectable={checkNodeSelectable}
          isNodeExpandable={isNodeExpandable}
          onExpandNode={handleOpenFolder}
          onUnexpandNode={handleCloseFolder}
          viewItemRenderer={renderViewItem}
          onSelectNodesChange={handleSelectNodes}
        />
      </div>
    );
  },
);

TreeView.defaultProps = {
  valueKey: 'id',
  disabledIds: [],
  selectedIds: [],
  dataViewProps: {
    dataFilter: undefined,
    enableFoldersSelection: false,
    allowWholeFolderSelection: false,
    rowIdsWithIndicator: [],
    renderIndicator: noop,
  },
};

TreeView.propTypes = {
  valueKey: PropTypes.oneOf(['urn', 'id']),
  entities: PropTypes.array.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  disabledIds: PropTypes.arrayOf(PropTypes.string),
  multiple: PropTypes.bool,
  setSelection: PropTypes.func,
  plugin: PropTypes.shape({
    getDefaultExpandedFolderUrn: PropTypes.func,
  }),
  dataViewProps: PropTypes.shape({
    dataFilter: PropTypes.func,
    enableFoldersSelection: PropTypes.bool,
    allowWholeFolderSelection: PropTypes.bool,
    rowIdsWithIndicator: PropTypes.array,
    renderIndicator: PropTypes.func,
  }),
};

TreeView.displayName = 'TreeView';

export default TreeView;
